import {
  DiscordIcon,
  ExternalLinkIcon,
  FacebookIcon,
  InstagramIcon,
  LinkedInIcon,
  SmileFaceIcon,
  TelegramIcon,
  TwitterIcon,
  WebsiteIcon,
} from "@assets";
import {
  Box,
  Icon,
  List,
  ListItem,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";
import { NextImage } from "@components/NextImage/NextImage";
import CalendarIcon from "@icons/CalendarIcon";
import LocationIcon from "@icons/LocationIcon";
import { EventType, NFTEvent, NFTEventInfo } from "@models/event";
import { compareText, toDuration } from "@models/utils";
import { isEmpty } from "lodash";
import EventDetailMap from "./EventDetailMap";

const GOOGLE_URL = "https://www.google.com/maps";

const ListItemWrapper = ({ imgSrc, imgAlt, children, align }: any) => (
  <ListItem
    display="flex"
    flexDir="row"
    alignItems={align ? align : "center"}
    justifyContent="flex-start"
    gap={"10px"}
    className="text-primary"
  >
    <NextImage className="w-8 svg-brown" src={imgSrc} alt={imgAlt} />
    <Box fontWeight={500} fontSize="1rem" textAlign="left" color="neutral.300">
      {children}
    </Box>
  </ListItem>
);

const EventDetailDateTime = ({
  event,
  info,
  iconClassName = "bg-white dark:bg-transparent rounded",
  iconContainerClassName = "dark:text-white text-primary",
}: {
  event: NFTEvent;
  info: NFTEventInfo;
  iconClassName?: string;
  iconContainerClassName?: string;
}) => {
  const isBenefitEvent = compareText(event.type, EventType.BENEFIT);
  const datetimeTitle = isBenefitEvent ? "Claiming Period" : "Date";

  let ticketDateTime = event.localePeriod();

  if (event && !isEmpty(event.tiers)) {
    const tier = event.tiers[0];
    ticketDateTime =
      !isEmpty(tier.paymentConfig) && isBenefitEvent
        ? toDuration(
            tier.paymentConfig.start,
            tier.paymentConfig.end,
            event.timezone
          )
        : ticketDateTime;
  }

  return (
    <div className="flex flex-row gap-3">
      <div className={iconContainerClassName}>
        <Icon as={CalendarIcon} boxSize={8} className={iconClassName} />
      </div>
      <div className="flex flex-col">
        <span className="text-black dark:text-white">{datetimeTitle}</span>
        <span className="text-primary">{ticketDateTime}</span>
      </div>
    </div>
  );
};

const EventLocation = ({
  info,
  iconClassName = "bg-white dark:bg-transparent rounded",
  iconContainerClassName = "dark:text-white text-primary",
}: {
  info: NFTEventInfo;
  iconClassName?: string;
  iconContainerClassName?: string;
}) => (
  <div className="flex flex-row gap-3">
    <div className={iconContainerClassName}>
      <Icon as={LocationIcon} boxSize={8} className={iconClassName} />
    </div>
    <div className="flex flex-col">
      <span className="text-[#000000] dark:text-white">Location</span>
      {info.locationUrl ? (
        <a href={info.locationUrl} target="_blank" rel="nofollow noopener">
          <Text textDecoration="underline" color={"primary.500"}>
            {info.location}
          </Text>
        </a>
      ) : (
        <Text color={"primary.500"}>{info.location}</Text>
      )}
    </div>
  </div>
);

const EventDetailHost = ({
  info,
  eventType,
}: {
  info: NFTEventInfo;
  eventType: EventType;
}) => (
  <ListItemWrapper imgSrc={SmileFaceIcon} imgAlt="simle-face icon">
    <Text color={useColorModeValue("#444444", "#DEDEE0")}>
      <i>{eventType === EventType.MEMBERSHIP ? "Created by " : "Hosted by "}</i>
      <i>
        {info.organizer.url ? (
          <a href={info.organizer.url} target="_blank" rel="noreferrer">
            <span className="text-primary-300 underline">
              {info.organizer.name}
            </span>
          </a>
        ) : (
          info.organizer.name
        )}
      </i>
    </Text>
  </ListItemWrapper>
);

const EventDetailExternalLinks = ({ info }: { info: NFTEventInfo }) => {
  const externalLinkIcons = {
    twitter: TwitterIcon,
    discord: DiscordIcon,
    linkedin: LinkedInIcon,
    instagram: InstagramIcon,
    telegram: TelegramIcon,
    facebook: FacebookIcon,
    website: WebsiteIcon,
  };
  const hasIcon = (name) => {
    return Object.keys(externalLinkIcons).includes(name);
  };
  const linksWithIcon =
    info.externalLinks && info.externalLinks.length > 0
      ? info.externalLinks.filter((link) => hasIcon(link.name.toLowerCase()))
      : [];
  const linksWithText =
    info.externalLinks && info.externalLinks.length > 0
      ? info.externalLinks.filter((link) => !hasIcon(link.name.toLowerCase()))
      : [];

  return (
    <ListItemWrapper imgSrc={ExternalLinkIcon} imgAlt="external-link icon">
      <div className="flex flex-row flex-wrap items-center">
        {linksWithIcon.length > 0 &&
          linksWithIcon.map((link, index) => {
            return (
              <a
                className="mr-5 text-neutral dark:text-white"
                href={link.url}
                target="_blank"
                rel="noreferrer"
                key={index}
              >
                <NextImage
                  className="w-5"
                  src={externalLinkIcons[link.name.toLowerCase()]}
                />
              </a>
            );
          })}
        {linksWithText.length > 0 &&
          linksWithText.map((link, index) => {
            return (
              <a
                className="mr-5 text-neutral dark:text-white"
                href={link.url}
                target="_blank"
                rel="noreferrer"
                key={index}
              >
                <Text
                  textDecoration="underline"
                  color={"primary.500"}
                  textTransform="capitalize"
                >
                  {link.name}
                </Text>
              </a>
            );
          })}
      </div>
    </ListItemWrapper>
  );
};

const EventDetailDateTimeLocation = ({
  event,
  info,
  containerClassName = "flex flex-col md:flex-row gap-6 mb-6 bg-primary-50 dark:backdrop-blur rounded-lg p-4 w-full dark:bg-white/[0.1]",
  iconClassName,
  iconContainerClassName,
}: {
  event: NFTEvent;
  info: NFTEventInfo;
  containerClassName?: string;
  iconClassName?: string;
  iconContainerClassName?: string;
}) => {
  return (
    <div className={containerClassName}>
      <EventDetailDateTime
        event={event}
        info={info}
        iconClassName={iconClassName}
        iconContainerClassName={iconContainerClassName}
      />

      {info.location && (
        <EventLocation
          info={info}
          iconClassName={iconClassName}
          iconContainerClassName={iconContainerClassName}
        />
      )}
    </div>
  );
};

const EventDetailTicketInfo = ({
  event,
  info,
  dateTimeLocationContainerClassName,
  dateTimeLocationIconClassName,
  dateTimeLocationIconContainerClassName,
}: {
  event: NFTEvent;
  info: NFTEventInfo;
  dateTimeLocationContainerClassName?: string;
  dateTimeLocationIconClassName?: string;
  dateTimeLocationIconContainerClassName?: string;
}) => {
  return (
    <div className="w-full">
      <EventDetailDateTimeLocation
        event={event}
        info={info}
        containerClassName={dateTimeLocationContainerClassName}
        iconClassName={dateTimeLocationIconClassName}
        iconContainerClassName={dateTimeLocationIconContainerClassName}
      />
      {info.location && <EventDetailMap info={info} event={event} />}
    </div>
  );
};

const EventDetailGeneralInfo = ({
  event,
  info,
}: {
  event: NFTEvent;
  info: NFTEventInfo;
}) => {
  return (
    <List spacing={3} className="w-full">
      {info.organizer && info.organizer.name && (
        <EventDetailHost info={info} eventType={event.type} />
      )}
      {info.externalLinks && info.externalLinks.length > 0 && (
        <EventDetailExternalLinks info={info} />
      )}
    </List>
  );
};

const EventDetailInfo = ({
  event,
  info,
  dateTimeLocationContainerClassName,
  dateTimeLocationIconClassName,
  dateTimeLocationIconContainerClassName,
}: {
  event: NFTEvent;
  info: NFTEventInfo;
  dateTimeLocationContainerClassName?: string;
  dateTimeLocationIconClassName?: string;
  dateTimeLocationIconContainerClassName?: string;
}) => {
  return (
    <List spacing={3} className="w-full">
      {event.type === EventType.TICKET && (
        <EventDetailTicketInfo
          event={event}
          info={info}
          dateTimeLocationContainerClassName={
            dateTimeLocationContainerClassName
          }
          dateTimeLocationIconClassName={dateTimeLocationIconClassName}
          dateTimeLocationIconContainerClassName={
            dateTimeLocationIconContainerClassName
          }
        />
      )}
      <EventDetailGeneralInfo event={event} info={info} />
    </List>
  );
};

export { EventDetailGeneralInfo, EventDetailInfo, EventDetailTicketInfo };
